import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import AppointmentForm from '@/v2/modals/appointment/AppointmentEdit/BaseForm'
import AppointmentValid from '@/v2/components/forms/Appointment/validator'
import formatAppointment from '@/v2/components/forms/Appointment/fromForm'
import TimeOffForm from '@/v2/components/forms/TimeOff/Form'
import TimeOffValid from '@/v2/components/forms/TimeOff/validator'
import formatTimeOff from '@/v2/components/forms/TimeOff/fromForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { errorsFromJson } from '@/v2/utils/forms'
import { useCreateAppointment } from '@/v2/hooks/useAppointment'
import { useAddTimeOff } from '@/v2/hooks/useTimeOffs'
import { useLoadData } from '@/v2/modals/event/hooks/useLoadData'
import { getAppointmentState, getTimeOffState } from '@/v2/modals/event/utils'
import { useUser } from '@/v2/hooks/useUser'
import { Container, Header, Content } from '../MobileAppointmentDetails/styles'
import styled from 'styled-components'

const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}

const TabsContainer = styled.div`
  display: flex;
  gap: 20px;

  & > button {
    position: relative;
    font-size: 16px;
    font-weight: 300;
    padding: 0;
    min-width: auto;
    flex-shrink: 0;
    white-space: nowrap;
  }

  & > button.selected {
    font-weight: 500;
  }

  & > button.selected::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    right: 0;
    height: 2px;
    background: black;
  }
`

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-left: calc(16px + env(safe-area-inset-left));
  padding-right: calc(16px + env(safe-area-inset-right));
  background: #fff;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  z-index: 102;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
`

const MobileNewEvent = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { onOpenSnackbar } = useSnackbarContext()
  const { user } = useUser()
  const isMounted = useRef(true)

  const { start, resourceId = '', virtual = false, patient } =
    location.state?.initialValues || {}

  const { loading, rooms, doctors, clinic, types } = useLoadData()

  const [tab, setTab] = useState('appointment')
  const [formError, setFormError] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const { createAppointment } = useCreateAppointment()
  const { addTimeOff } = useAddTimeOff()

  const columnInfo = resourceId ? resourceId.split('-') : []

  const appointmentInitialState = getAppointmentState({
    ...(resourceId ? { [`${columnInfo[0]}Id`]: columnInfo[1] } : {}),
    ...(patient ? { registeredPatient: patient } : {}),
    ...(patient ? { contactMethod: patient.contactMethod } : {}),
    startDate: start,
    virtual,
  })
  const [appointment, setAppointment] = useState(appointmentInitialState)

  const timeOffInitialState = getTimeOffState(clinic, {
    ...(resourceId ? { [`${columnInfo[0]}Id`]: columnInfo[1] } : {}),
    startDate: start,
  })
  const [timeOff, setTimeOff] = useState(timeOffInitialState)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (clinic) {
      setTimeOff(state => ({ ...state, clinicId: clinic }))
    }
  }, [clinic])

  const handleChangeAppointment = items => {
    if (!isMounted.current) return
    const { newPatient, ...values } = items
    setAppointment(prev => ({
      ...prev,
      ...values,
      newPatient: {
        ...prev.newPatient,
        ...newPatient,
      },
    }))
  }

  const handleBack = () => {
    if (
      (tab === 'appointment' &&
        !isEqual(appointmentInitialState, appointment)) ||
      (tab === 'timeoff' && !isEqual(timeOffInitialState, timeOff))
    ) {
      const shouldLeave = window.confirm(
        'Are you sure you want to leave without saving changes?'
      )
      if (shouldLeave) {
        if (location.state?.backgroundLocation) {
          navigate(location.state.backgroundLocation.pathname)
        } else {
          navigate(-1)
        }
      }
    } else {
      if (location.state?.backgroundLocation) {
        navigate(location.state.backgroundLocation.pathname)
      } else {
        navigate(-1)
      }
    }
  }

  const handleCreateAppointment = async () => {
    const validErrors = AppointmentValid(appointment)
    if (isMounted.current) {
      setFormError(validErrors || {})
    }

    if (!validErrors) {
      if (isMounted.current) {
        setIsLoading(true)
      }
      try {
        const response = await createAppointment(formatAppointment(appointment))
        if (response.error) {
          const errorMsg =
            typeof response.error === 'object'
              ? Object.values(response.error)[0]
              : response.error
          onOpenSnackbar(
            errorMsg || 'Appointment could not be created. Please try again.',
            'error'
          )
          return
        }

        if (isMounted.current) {
          onOpenSnackbar('Appointment created.')

          if (window.analytics && window.analytics.track) {
            window.analytics.track('New appointment', {
              category: 'Clearworks',
              label: 'New appointment from staff',
              datetime: response.startsAt,
              appointmentType: response.appointmentType?.title,
              room: response.room?.title || 'virtual',
            })
          }

          // Navigate to the appointment details
          navigate(`/v2/appointment/${response.id}`, {
            state: { backgroundLocation: location.state?.backgroundLocation },
          })
        }
      } catch (error) {
        console.error('Appointment creation error:', error)
        if (isMounted.current) {
          const asyncErrors = error.response?.errors
            ? errorsFromJson(error.response.errors)
            : errorsFromJson(error.message)

          if (asyncErrors) {
            const { patient, ...otherErrors } = asyncErrors
            const fieldName =
              appointment.patientType === 'new'
                ? 'newPatient'
                : 'registeredPatient'
            setFormError({
              ...otherErrors,
              [fieldName]: {
                ...patient,
                phone: (patient?.phoneNumber || [])[0],
              },
            })
            onOpenSnackbar('Something went wrong!', 'error')
          } else {
            const errorMessage = error.message || 'Something went wrong!'
            onOpenSnackbar(errorMessage, 'error')
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const handleCreateTimeOff = async () => {
    const validErrors = TimeOffValid(timeOff)
    if (isMounted.current) {
      setFormError(validErrors || {})
    }

    if (!validErrors) {
      if (isMounted.current) {
        setIsLoading(true)
      }
      try {
        await addTimeOff(formatTimeOff(timeOff))
        if (isMounted.current) {
          onOpenSnackbar('Blocked time created.')
          if (location.state?.backgroundLocation) {
            navigate(location.state.backgroundLocation.pathname)
          } else {
            navigate(-1)
          }
        }
      } catch (error) {
        console.error('Time off creation error:', error)
        if (isMounted.current) {
          const asyncErrors = error.response?.errors
            ? errorsFromJson(error.response.errors)
            : errorsFromJson(error.message)

          if (asyncErrors) {
            setFormError(asyncErrors)
            onOpenSnackbar('Something went wrong!', 'error')
          } else {
            const errorMessage = error.message || 'Something went wrong!'
            onOpenSnackbar(errorMessage, 'error')
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const handleSubmit = () => {
    if (tab === 'appointment') {
      handleCreateAppointment()
    } else {
      handleCreateTimeOff()
    }
  }

  if (loading) {
    return (
      <Container>
        <Header>
          <Typography variant="h2" fontSize="16px" fontWeight="500">
            Loading...
          </Typography>
          <IconButton onClick={handleBack} size="small">
            <CloseIcon />
          </IconButton>
        </Header>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <TabsContainer>
          <Button
            variant="text"
            className={tab === 'appointment' ? 'selected' : ''}
            onClick={() => setTab('appointment')}
          >
            Appointment
          </Button>
          {user?.admin && (
            <Button
              variant="text"
              className={tab !== 'appointment' ? 'selected' : ''}
              onClick={() => setTab('timeoff')}
            >
              Blocked time
            </Button>
          )}
        </TabsContainer>
        <IconButton onClick={handleBack} size="small">
          <CloseIcon />
        </IconButton>
      </Header>

      <Content style={{ paddingBottom: '100px' }}>
        {tab === 'appointment' ? (
          <AppointmentForm
            error={formError}
            values={appointment}
            canRemove={true}
            types={types}
            rooms={rooms}
            doctors={doctors}
            onChange={handleChangeAppointment}
          />
        ) : (
          <TimeOffForm
            values={timeOff}
            clinic={clinic}
            rooms={rooms.map(({ id, title }) => ({
              value: id,
              label: title,
            }))}
            doctors={doctors.map(({ id, fullName }) => ({
              value: id,
              label: fullName,
            }))}
            error={formError}
            onChange={value => setTimeOff({ ...timeOff, ...value })}
          />
        )}
      </Content>

      <Footer>
        <Button
          variant="text"
          onClick={handleBack}
          disabled={isLoading}
          sx={{ color: '#323232' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </Footer>
    </Container>
  )
}

export default MobileNewEvent
